import React from "react";
import { ActivityIndicator, StyleSheet, Switch, Text, View } from "react-native";

interface SchedulingPreferenceToggleProps {
  /** Current "offer scheduled sessions" value. */
  value: boolean;
  /** Called when the host flips the switch. Should resolve once the
   *  backend has acknowledged the change so the loading spinner clears. */
  onToggle: (next: boolean) => void | Promise<void>;
  /** External loading state — disables the switch and shows a spinner. */
  isLoading?: boolean;
  /** Optional disabled state (e.g. while the parent form is updating). */
  disabled?: boolean;
}

/**
 * Host-facing per-charger toggle that enables or disables scheduled
 * sessions for a single charger. Visibility should be gated by the
 * caller via `supportsOcpp201(charger)` — Emporia and OCPP 1.6 chargers
 * never expose this control.
 */
const SchedulingPreferenceToggle: React.FC<SchedulingPreferenceToggleProps> = ({
  value,
  onToggle,
  isLoading,
  disabled,
}) => {
  const handleChange = (next: boolean) => {
    if (isLoading || disabled) return;
    onToggle(next);
  };

  return (
    <View style={styles.wrapper}>
      <View style={styles.row}>
        <View style={styles.textWrap}>
          <Text style={styles.label}>Offer scheduled sessions</Text>
          <Text style={styles.helper}>
            Let drivers reserve a time window on this charger. Available on
            OCPP 2.0.1 chargers.
          </Text>
        </View>
        <View style={styles.controlWrap}>
          {isLoading ? (
            <ActivityIndicator size="small" color="#39BEB7" />
          ) : (
            <Switch
              value={value}
              onValueChange={handleChange}
              disabled={disabled}
              trackColor={{ false: "#3A3D42", true: "#39BEB750" }}
              thumbColor={value ? "#39BEB7" : "#8D8E90"}
              ios_backgroundColor="#3A3D42"
            />
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    marginBottom: 16,
    backgroundColor: "#1A1D21",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  textWrap: {
    flex: 1,
    paddingRight: 12,
  },
  label: {
    color: "#fff",
    fontWeight: "600",
    fontSize: 15,
    marginBottom: 4,
  },
  helper: {
    color: "#8D8E90",
    fontSize: 12,
    lineHeight: 16,
  },
  controlWrap: {
    minWidth: 50,
    alignItems: "flex-end",
    justifyContent: "center",
  },
});

export default SchedulingPreferenceToggle;
